import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { setupInterceptors } from './interceptors';
import { requireAuthToken } from './session';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'https://server.srt.carewinners.com/api/v1';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

setupInterceptors(axiosInstance);

const extractData = <T>(response: { data: T }): T => response.data;

const httpClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) => axiosInstance.get<T>(url, config).then(extractData),
  post: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.post<T>(url, payload, config).then(extractData),
  put: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.put<T>(url, payload, config).then(extractData),
  patch: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.patch<T>(url, payload, config).then(extractData),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete<T>(url, config).then(extractData),
};

export type ApiResult<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: string;
    };

const formatError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return (error.response?.data as { message?: string })?.message ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unexpected API error';
};

const handleApiCall = async <T>(call: () => Promise<T>): Promise<ApiResult<T>> => {
  try {
    const result = await call();
    return { success: true, data: result };
  } catch (error) {
    console.error('api-client error', error);
    return { success: false, error: formatError(error) };
  }
};

export interface SignupPayload {
  name: string;
  email: string;
  password: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export const authApi = {
  signup: (payload: SignupPayload) =>
    handleApiCall(() =>
      httpClient.post<Record<string, unknown>>('/auth/signup', payload),
    ),
  verify: (token: string) =>
    handleApiCall(() =>
      httpClient.get<Record<string, unknown>>(`/auth/verify?token=${encodeURIComponent(token)}`),
    ),
  login: (payload: LoginPayload) =>
    handleApiCall(() => httpClient.post<Record<string, unknown>>('/auth/login', payload)),
};

export const agentsApi = {
  list: (page = 1, limit = 20) =>
    handleApiCall(() =>
      httpClient.get<AgentsResponse>(`/agents?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
};

export const chatsApi = {
  list: (page = 1, limit = 20) =>
    handleApiCall(() =>
      httpClient.get<Record<string, unknown>>(`/chats?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
  messages: (externalId: string, page = 1, limit = 20) =>
    handleApiCall(() =>
      httpClient.get<Record<string, unknown>>(`/chats/${encodeURIComponent(externalId)}/messages?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
  sendMessage: (externalId: string, payload: {
    senderType: 'agent';
    senderId: string;
    messageType: 'text' | 'file';
    content?: string;
    attachmentUrl?: string;
    attachmentName?: string;
    attachmentSize?: number;
    mimeType?: string;
  }) =>
    handleApiCall(() =>
      httpClient.post<Record<string, unknown>>(`/chats/${encodeURIComponent(externalId)}/messages`, payload, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
};

export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions?: string[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Agent {
  id: string;
  name?: string;
  email?: string;
  isActive?: boolean;
  isAdmin?: boolean;
}

export interface AgentsResponse {
  data: Agent[];
  meta?: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface RolesResponse {
  data: Role[];
  meta?: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface CreateRolePayload {
  name: string;
  description?: string;
  permissions?: string[];
  isDefault?: boolean;
}

export interface RolesPermissionsResponse {
  data: string[];
}

export interface AssignRoleResponse {
  roleId: string;
  agentId: string;
}

export const agentRolesApi = {
  list: (page = 1, limit = 20) =>
    handleApiCall(() =>
      httpClient.get<RolesResponse>(`/roles?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
  create: (payload: CreateRolePayload) =>
    handleApiCall(() =>
      httpClient.post<Role>('/roles', payload, {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
  assign: (roleId: string, agentId: string) =>
    handleApiCall(() =>
      httpClient.post<AssignRoleResponse>(
        `/roles/${encodeURIComponent(roleId)}/assign`,
        { agentId },
        {
          headers: {
            Authorization: `Bearer ${requireAuthToken()}`,
          },
        },
      ),
    ),
  permissions: () =>
    handleApiCall(() =>
      httpClient.get<RolesPermissionsResponse>('/roles/permissions', {
        headers: {
          Authorization: `Bearer ${requireAuthToken()}`,
        },
      }),
    ),
};

export const apiClient = {
  ...httpClient,
};
